import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { calcularMes, isoAMes, mesAIso } from '@leadsphere/types';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { useApi } from '../../hooks/useApi.js';
import { useClientesLookup } from '../../hooks/useClientesLookup.js';
import type { CatalogoArea } from '../catalogos/types.js';
import { EstatusBadge, PrioridadBadge } from './badges.js';
import type { ClienteResumen, ListarTicketsResponse, Ticket } from './types.js';
import { useTicketsTodos } from './useTicketsTodos.js';

const LIMIT = 25;

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function fmtFechaHora(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function SlaCell({ cumple }: { cumple: boolean | null }) {
  if (cumple === null) return <span className="sla-pend">—</span>;
  return cumple ? <span className="sla-ok">✓ Sí</span> : <span className="sla-fail">✗ No</span>;
}

// Prompt AJ — todos los clientes deben ser visibles sin truncar en
// silencio: se muestran hasta 2 badges y, si hay más, un badge "+N más" con
// el resto listado en el title (tooltip nativo, mismo criterio ligero que
// el resto de la app — no hay librería de tooltips instalada).
const CLIENTES_VISIBLES = 2;

function ClientesCell({ esExterno, clientesTicket }: { esExterno: boolean; clientesTicket: ClienteResumen[] }) {
  if (!esExterno) return <span style={{ color: 'var(--muted)', fontSize: 12 }}>Interno</span>;
  if (!clientesTicket.length) return <span style={{ color: 'var(--muted)', fontSize: 12 }}>—</span>;

  const visibles = clientesTicket.slice(0, CLIENTES_VISIBLES);
  const resto = clientesTicket.slice(CLIENTES_VISIBLES);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {visibles.map(c => (
        <Badge key={c.id} tone="info">{c.nombre_comercial}</Badge>
      ))}
      {resto.length > 0 && (
        <span title={resto.map(c => c.nombre_comercial).join(', ')}>
          <Badge tone="neutral">+{resto.length} más</Badge>
        </span>
      )}
    </div>
  );
}

function CsatCell({ valor }: { valor: number | null }) {
  if (!valor) return <span style={{ color: 'var(--muted)', fontSize: 12 }}>Sin calificar</span>;
  return (
    <span>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= valor ? '#f59e0b' : '#e2e8f0', fontSize: 16 }}>
          ★
        </span>
      ))}
      <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4 }}>{valor}/5</span>
    </span>
  );
}

function calcularStats(tickets: Ticket[]) {
  const mesActual = calcularMes(new Date());
  const totalMes = tickets.filter(t => t.mes === mesActual).length;
  const enProgreso = tickets.filter(t => ['nueva', 'en_progreso', 'reabierta'].includes(t.estatus)).length;

  const conSlaResp = tickets.filter(t => t.cumple_sla_resp !== null);
  const pctSlaResp = conSlaResp.length ? Math.round((conSlaResp.filter(t => t.cumple_sla_resp).length / conSlaResp.length) * 100) : null;

  const conCsat = tickets.filter(t => t.csat !== null);
  const csatProm = conCsat.length ? (conCsat.reduce((acc, t) => acc + t.csat!, 0) / conCsat.length).toFixed(1) : null;

  return { totalMes, enProgreso, pctSlaResp, csatProm };
}

export function TicketsPage() {
  const { get } = useApi();
  const navigate = useNavigate();

  // Prompt AA — el filtro de Área se puebla desde el catálogo real, ya no
  // desde el enum estático AREAS.
  const { data: areas } = useQuery({
    queryKey: ['catalogo-area'],
    queryFn: () => get<CatalogoArea[]>('/catalogos/area'),
  });

  // Prompt AJ — lista de clientes para el filtro (dropdown simple; la lista
  // de clientes no es lo bastante larga todavía para justificar un
  // autocomplete aparte del <select> nativo, mismo criterio que el resto de
  // filtros de esta página).
  const { data: clientesData } = useClientesLookup();
  const clientesOpts = clientesData?.data ?? [];

  const ahora = new Date();

  const [search, setSearch] = useState('');
  const [area, setArea] = useState('');
  const [estatus, setEstatus] = useState('');
  const [prio, setPrio] = useState('');
  // Prompt AJ — filtro por cliente (join contra ticket_clientes en backend)
  // y por Interno/Externo.
  const [clienteId, setClienteId] = useState('');
  const [externo, setExterno] = useState('');
  // Precargado al mes en curso (año + mes por separado, mismo patrón de 2
  // selects que CxC) — se combinan vía isoAMes al formato canónico del backend.
  const [anioFiltro, setAnioFiltro] = useState(String(ahora.getFullYear()));
  const [mesNumFiltro, setMesNumFiltro] = useState(String(ahora.getMonth() + 1).padStart(2, '0'));
  const [page, setPage] = useState(1);

  // Los stat-cards y el % SLA/CSAT promedio ven TODOS los tickets, no solo
  // la página filtrada actual.
  const { data: statsData } = useTicketsTodos();
  const stats = calcularStats(statsData?.data ?? []);

  const aniosDisponibles = useMemo(() => {
    const anios = new Set((statsData?.data ?? []).map(t => Number(mesAIso(t.mes).slice(0, 4))));
    anios.add(ahora.getFullYear());
    return [...anios].sort((a, b) => b - a);
  }, [statsData]);

  const mes = anioFiltro && mesNumFiltro ? isoAMes(`${anioFiltro}-${mesNumFiltro}`) : '';

  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (area) params.set('area', area);
  if (estatus) params.set('estatus', estatus);
  if (prio) params.set('prio', prio);
  if (mes) params.set('mes', mes);
  if (clienteId) params.set('cliente_id', clienteId);
  if (externo) params.set('externo', externo);
  params.set('page', String(page));
  params.set('limit', String(LIMIT));

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', params.toString()],
    queryFn: () => get<ListarTicketsResponse>(`/tickets?${params.toString()}`),
  });

  const filas = data?.data ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.meta.total / data.meta.limit)) : 1;
  const hasFilters = Boolean(search || area || estatus || prio || anioFiltro || mesNumFiltro || clienteId || externo);

  function resetFiltros() {
    setSearch('');
    setArea('');
    setEstatus('');
    setPrio('');
    setAnioFiltro('');
    setMesNumFiltro('');
    setClienteId('');
    setExterno('');
    setPage(1);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Tickets</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>Solicitudes internas entre áreas</div>
        </div>
        <Button onClick={() => navigate('/tickets/nuevo')}>Nuevo Ticket</Button>
      </div>

      <div className="stats-grid">
        <div className="stat-card hl">
          <div className="stat-label">Total este mes</div>
          <div className="stat-value">{stats.totalMes}</div>
          <div className="stat-sub">tickets registrados</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">En progreso</div>
          <div className="stat-value" style={{ color: 'var(--warning)' }}>{stats.enProgreso}</div>
          <div className="stat-sub">requieren atención</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">% SLA Resp.</div>
          <div className="stat-value success">{stats.pctSlaResp === null ? '—' : `${stats.pctSlaResp}%`}</div>
          <div className="stat-sub">cumplimiento</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">CSAT promedio</div>
          <div className="stat-value">{stats.csatProm === null ? '—' : `⭐ ${stats.csatProm}`}</div>
          <div className="stat-sub">satisfacción del equipo</div>
        </div>
      </div>

      <div className="filters-bar">
        <input
          className="filter-input filter-search"
          type="text"
          placeholder="Buscar folio, solicitante, título..."
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="filter-input"
          value={area}
          onChange={e => {
            setArea(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Área</option>
          {(areas ?? []).map(a => (
            <option key={a.id} value={a.nombre}>
              {a.nombre}
            </option>
          ))}
        </select>
        <select
          className="filter-input"
          value={estatus}
          onChange={e => {
            setEstatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Estatus</option>
          <option value="nueva">Nueva</option>
          <option value="en_progreso">En progreso</option>
          <option value="resuelta">Resuelta</option>
          <option value="reabierta">Reabierta</option>
          <option value="cancelada">Cancelada</option>
        </select>
        <select
          className="filter-input"
          value={prio}
          onChange={e => {
            setPrio(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Prioridad</option>
          <option value="P1">P1 Urgente</option>
          <option value="P2">P2 Normal</option>
          <option value="P3">P3 Programable</option>
        </select>
        <select
          className="filter-input"
          value={anioFiltro}
          onChange={e => {
            setAnioFiltro(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Año</option>
          {aniosDisponibles.map(a => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          className="filter-input"
          value={mesNumFiltro}
          onChange={e => {
            setMesNumFiltro(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Mes</option>
          {MESES.map((m, i) => (
            <option key={m} value={String(i + 1).padStart(2, '0')}>
              {m}
            </option>
          ))}
        </select>
        <select
          className="filter-input"
          value={externo}
          onChange={e => {
            setExterno(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Interno / Externo</option>
          <option value="false">Interno</option>
          <option value="true">Externo</option>
        </select>
        <select
          className="filter-input"
          value={clienteId}
          onChange={e => {
            setClienteId(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Cliente</option>
          {clientesOpts.map(c => (
            <option key={c.id} value={c.id}>
              {c.nombre_comercial}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button className="filter-reset" onClick={resetFiltros}>
            ✕ Limpiar
          </button>
        )}
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Título</th>
                <th>Área Resp.</th>
                <th>Responsable</th>
                <th>Cliente(s)</th>
                <th>Prioridad</th>
                <th>Estatus</th>
                <th>Creado</th>
                <th>SLA Resp.</th>
                <th>SLA Reso.</th>
                <th>CSAT</th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={11}>
                    {isLoading ? 'Cargando…' : 'No se encontraron tickets'}
                  </td>
                </tr>
              ) : (
                filas.map(t => (
                  <tr key={t.id}>
                    <td>
                      <span className="tk-folio" onClick={() => navigate(`/tickets/${t.id}`)}>
                        {t.folio}
                      </span>
                    </td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.titulo}</td>
                    <td style={{ color: 'var(--muted)' }}>{t.area_responsable}</td>
                    <td style={{ color: 'var(--muted)' }}>{t.responsables.length ? t.responsables.map(r => r.nombre).join(', ') : '—'}</td>
                    <td>
                      <ClientesCell esExterno={t.nivel_servicio_es_externo} clientesTicket={t.clientes} />
                    </td>
                    <td>
                      <PrioridadBadge prioridad={t.prioridad} />
                    </td>
                    <td>
                      <EstatusBadge estatus={t.estatus} />
                    </td>
                    <td style={{ color: 'var(--muted)', fontSize: 12 }}>{fmtFechaHora(t.fecha_creacion)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <SlaCell cumple={t.cumple_sla_resp} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <SlaCell cumple={t.cumple_sla_reso} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <CsatCell valor={t.csat} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="table-footer">
          <span className="table-count">{data ? `${filas.length} de ${data.meta.total} ticket${data.meta.total !== 1 ? 's' : ''}` : '—'}</span>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}
